<template>

  <v-container fluid :style="(isAuthenticatedSession) ? 'margin-top:37px;' : 'margin-top:77px;'" class="page-track-order">

      <!------------------------------------------->
      <!-- Page Title and Tracking Number Prompt -->
      <!------------------------------------------->
      <v-container fluid class="pa-0">
        <v-row>
          <v-col>
            <div class="page-title">
              Track {{ appName }} Order
            </div>
          </v-col>
        </v-row>
        <v-row align="center" justify="center" class="mt-5">
          <v-col cols="10" xs="10" sm="7" md="4" lg="3" class="d-flex align-center justify-center">
            <v-text-field
                outlined
                hide-details
                label="Enter Tracking #"
                @keyup.enter="trackOrder"
                v-model="trackingNumber" />
          </v-col>
        </v-row>
        <v-row class="mt-3">
          <v-col>
            <v-btn
                large
                color="primary"
                class="mt-1"
                width="200"
                @click="trackOrder">
              Track
            </v-btn>
          </v-col>
        </v-row>
      </v-container>

      <!---------------------->
      <!-- Tracking Results -->
      <!---------------------->
      <!-- (data loading spinner) -->
      <v-container v-if="isProcessing" class="d-flex flex-column align-left justify-center mt-4 mb-6 ml-8">
        <v-progress-circular indeterminate :size="50" color="primary" />
      </v-container>

      <v-container class="mt-6" v-else-if="activeErrorMessage">
        <v-icon color="red darken-4" style="margin-bottom:3px;margin-right:6px;">mdi-alert-box-outline</v-icon>
        <span style="color:#b71c1c">{{ activeErrorMessage }}</span>
      </v-container>

      <v-container class="results-container mt-6" v-else-if="order">
        <!-- (divider line) -->
        <!-- <v-row class="divider mt-4 mb-6 ml-1"></v-row> -->

        <div class="mt-4 mb-6 ml-3 title">
          Order Details
        </div>
        <!-- (order identification) -->
        <v-row class="mt-3 ml-4">
          <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
            Order Number:
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0" >
            {{ orderNumber }}
          </v-col>
        </v-row>
        <v-row class="ml-4 mt-5">
          <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
            Provider:
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
            {{ providerName }}
          </v-col>
        </v-row>
        <v-row class="ml-4 mt-5" v-if="accountName">
          <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
            Account:
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
            {{ accountName }}
          </v-col>
        </v-row>
        <v-row class="ml-4 mt-5" v-if="brandName">
          <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
            Brand:
          </v-col>
          <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
            {{ brandName }}
          </v-col>
        </v-row>

        <!-- (estimated pickup/delivery times) -->
        <template v-if="showEstimatedTimes">
          <v-row class="mt-8 ml-4">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Delivery for:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              {{ deliveryDate }}
            </v-col>
          </v-row>
          <v-row class="ml-4">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Est. Pickup:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              {{ estimatedPickupTime }}
            </v-col>
          </v-row>
          <v-row class="ml-4">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Est. Delivery:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              {{ estimatedDeliveryTime }}
            </v-col>
          </v-row>
        </template>

        <!-- (credit status) -->
        <template v-if="isCredit">
          <v-row class="ml-4 mt-9">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Order Type:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              <div>CREDIT TO RECEIVER</div>
            </v-col>
          </v-row>
          <v-row class="ml-4 mt-4">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Credit Status:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              <div style="font-weight:500;">{{ orderStatus }}</div>
            </v-col>
          </v-row>
        </template>

        <!-- (order status) -->
        <template v-else>
          <v-row class="ml-4 mt-9">
            <v-col cols="12" xs="12" sm="12" md="3" lg="3" class="pa-0 ma-0 field-label">
              Order Status:
            </v-col>
            <v-col cols="12" xs="12" sm="12" md="9" lg="9" class="pa-0 ma-0">
              <div style="font-weight:500;">{{ orderStatus }}</div>
            </v-col>
          </v-row>
        </template>

        <!-- (order tracking history) -->
        <order-tracking-history
            :order="order"
            :isCredit="isCredit"
            :shortenNames="true"
            class="mt-8" />

      </v-container>

  </v-container>

</template>

<script>
import OrderTrackingHistory from '@/components/orders/OrderTrackingHistory'

export default {
  name: 'TrackOrderPage',

  components: {
    OrderTrackingHistory
  },

  data () {
    return {
      trackingNumber: null,
      isProcessing: false,
      order: null,
      activeErrorMessage: false
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    isAuthenticatedSession () {
      return this.$store.getters['auth/isAuthenticated']
    },
    orderNumber () {
      return this.order.thirdparty_order_number
    },
    orderStatus () {
      return this.order.order_status
    },
    // Identifies if this order is an issued credit
    isCredit () {
      return this.order.order_status === 'DELIVERED' && !this.order.route_id
    },
    providerName () {
      return (this.order.provider_alias)
        ? this.$root.providerAliasToName(this.order.provider_alias)
        : ''
    },
    accountName () {
      return this.order.thirdparty_account_name
    },
    brandName () {
      return this.order.thirdparty_brand_name
    },
    deliveryDate () {
      if (this.orderStatus === 'PLACED') return this.$root.utils.format(this.order.expected_delivery, 'MMMM D, YYYY')
      else {
        return (this.order.scheduled_delivery)
          ? this.$root.utils.format(this.order.scheduled_delivery, 'MMMM D, YYYY')
          : 'TBD'
      }
    },
    showEstimatedTimes () {
      return (this.orderStatus !== 'REJECTED_ON_DELIVERY' &&
             this.orderStatus !== 'CANCELED' &&
             this.orderStatus !== 'DELIVERED')
    },
    estimatedPickupTime () {
      return (this.order.estimated_shipper_arrival_time)
        ? this.$root.utils.format(this.order.estimated_shipper_arrival_time, 'h:mm a z')
        : 'TBD'
    },
    estimatedDeliveryTime () {
      return (this.order.estimated_receiver_arrival_time)
        ? this.$root.utils.format(this.order.estimated_receiver_arrival_time, 'h:mm a z')
        : 'TBD'
    }
  },

  beforeMount () {
    // Load order provided in url
    this.trackingNumber = this.$route.params.trackingNumber ? this.$route.params.trackingNumber : null
    if (this.trackingNumber) this.trackOrder()
  },

  methods: {
    reset () {
      this.order = null
      this.activeErrorMessage = null
    },

    async trackOrder () {
      if (!this.trackingNumber) return false

      this.isProcessing = true
      this.reset()

      const sanitized = this.trackingNumber.replace(/\s/g, '')

      // Load the public track order info
      try {
        this.order = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${sanitized}/track`
        })
      } catch (err) {
        this.activeErrorMessage = 'The order cannot be found.'
      }

      this.isProcessing = false
    }
  }
}
</script>

<style scoped>
  .page-track-order {
    text-align: center;
  }

  .page-track-order .page-title {
    font-size: 1.6rem;
    color: #d9d9d9;
  }

  .page-track-order .field-label {
    color: #9a9a9a;
  }

  .page-track-order .divider {
    border-bottom: 1px solid #525252;
  }

  .page-track-order .results-container {
    border: 1px solid #525252;
    border-radius: 4px;
    text-align: left;
    max-width: 640px;
  }
</style>
